import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { Bio } from '../side-panel/Bio';
import { Designations } from '../side-panel/Designations';
import { YoutubeIcon, LinkedInIcon, LinkIcon } from 'assets/images';
import { UnifiedProfileQuery$data } from 'graphql/__generated__/UnifiedProfileQuery.graphql';
import { readInlineData, useFragment, useLazyLoadQuery } from 'react-relay';
import { UNIFIED_PROFILE_FRAGMENT } from 'graphql/UnifiedProfileFragment';
import {
  UnifiedProfileFragment$data,
  UnifiedProfileFragment$key,
} from 'graphql/__generated__/UnifiedProfileFragment.graphql';
import {
  useMapProducts,
  formatUrlForDisplay,
  hasProfileConsented,
} from 'utils';
import useIsPersonalView from 'utils/useIsPersonalView';
import { usePathParam } from 'react-resource-router';
import SubSection from 'components/common/section/sub-section/SubSection';
import EmptyStateMessageBody from 'components/common/section/sub-section/EmptyStateMessageBody';
import { Products } from './products/Products';
import { NormalizedProduct, useNormalizedProducts } from 'utils/useMapProducts';
import {
  AtlassianProductsFragment$data,
  AtlassianProductsFragment$key,
} from 'graphql/__generated__/AtlassianProductsFragment.graphql';
import { ATLASSIAN_PRODUCTS_FRAGMENT } from 'graphql/AtlassianProductsFragment';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import { sendUIEvent } from 'services/analytics';
import { SCREEN_NAME, CERTIFICATE_STATUS } from 'constants/index';
import {
  processCertifications,
  Certification,
} from '../../../utils/certifications';
import useCertmetricsCertificates from 'utils/useCertmetricsCertificates';
import useIsProfileBanned from 'utils/useIsProfileBanned';
import useIsLoggedInUserAForumsModerator from 'utils/useIsLoggedInUserAForumsModerator';
import { UNIFIED_CONSENT_QUERY } from 'graphql/UnifiedConsentQuery';
import { UnifiedConsentQuery } from 'graphql/__generated__/UnifiedConsentQuery.graphql';

const AboutMe = ({ data }: { data: UnifiedProfileQuery$data }) => {
  const [unifiedProfileUsername] = usePathParam('pathProfileId');
  const isPersonalView = useIsPersonalView(unifiedProfileUsername as string);
  const isProfileBanned = useIsProfileBanned(unifiedProfileUsername as string);
  const isLoggedInUserModerator = useIsLoggedInUserAForumsModerator();

  const unifiedProfile = data?.unified?.unifiedProfile;
  const profileData =
    unifiedProfile && `__fragments` in unifiedProfile
      ? readInlineData<UnifiedProfileFragment$key>(
          UNIFIED_PROFILE_FRAGMENT,
          data?.unified?.unifiedProfile as NonNullable<
            UnifiedProfileQuery$data['unified']
          >['unifiedProfile'],
        )
      : null;
  const atlassianProducts = data?.unified?.atlassianProducts;
  const productData: AtlassianProductsFragment$data | null =
    atlassianProducts && `__fragments` in atlassianProducts
      ? useFragment<AtlassianProductsFragment$key>(
          ATLASSIAN_PRODUCTS_FRAGMENT,
          data.unified?.atlassianProducts,
        )
      : null;
  const allProducts: NormalizedProduct[] =
    productData && productData.edges ? useNormalizedProducts(productData) : [];

  const selectedProducts = profileData?.products
    ? useMapProducts(profileData?.products, allProducts)
    : [];

  const isBioDataEmpty = !profileData?.bio?.trim();

  const isProfileDataEmpty =
    !profileData ||
    (isBioDataEmpty &&
      !hasWhereToFindMe(profileData) &&
      !selectedProducts.length);

  if (!isPersonalView && isProfileDataEmpty) {
    return null;
  }

  const sendEditProfileAnalytics = () => {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'aboutMeEditProfile',
      isPersonalView,
      source: SCREEN_NAME.PROFILE,
    });
  };

  const { certificates } = useCertmetricsCertificates({
    username: profileData?.username ?? '',
    status: CERTIFICATE_STATUS.ACTIVE,
    type: ['STANDING'],
    sortField: 'ACTIVE_DATE',
    sortDirection: 'DESC',
  });

  const rawCertificates = certificates ?? [];

  const filteredCertificationData: Certification[] | null =
    processCertifications(rawCertificates, true);

  let certificationDesignations: Certification[] = [];

  const seenNames = new Set();

  filteredCertificationData.forEach((certification) => {
    if (!seenNames.has(certification.name)) {
      seenNames.add(certification.name);
      certificationDesignations.push(certification);
    }
  });

  const profileConsentData = useLazyLoadQuery<UnifiedConsentQuery>(
    UNIFIED_CONSENT_QUERY,
    { aaid: profileData?.aaid ?? '' },
    { fetchPolicy: 'store-or-network' },
  );

  const profileHasConsented = hasProfileConsented(
    profileConsentData.unified?.consent?.getConsent?.consentObj ?? [],
  );

  if (!profileHasConsented) {
    certificationDesignations = [];
  }

  const shouldRenderContent =
    isPersonalView && (!isProfileBanned || isLoggedInUserModerator);

  const certificationSection =
    profileHasConsented && certificationDesignations.length > 0 ? (
      <Designations certifications={certificationDesignations} />
    ) : null;

  const bioSection =
    profileData && !isBioDataEmpty ? (
      <SubSection title="About Me">
        <Bio
          htmlText={profileData.bio as string}
          isPersonalView={isPersonalView}
        />
      </SubSection>
    ) : isBioDataEmpty && shouldRenderContent ? (
      <SubSection title="About Me">
        <EmptyStateMessageBody
          message="Tell others about yourself and your skills."
          linkTo={`/profile/${unifiedProfileUsername}/edit`}
          linkType="internal"
          sendAnalyticsEvent={sendEditProfileAnalytics}
          linkText="Edit profile"
        />
      </SubSection>
    ) : null;

  const productsSection =
    selectedProducts.length > 0 ? (
      <SubSection title="My Products">
        <Products
          products={selectedProducts}
          isPersonalView={isPersonalView}
          source={SCREEN_NAME.PROFILE}
        />
      </SubSection>
    ) : null;

  const whereToFindMeSection =
    profileData && hasWhereToFindMe(profileData) ? (
      <SubSection title="Where to Find Me">
        <WhereToFindMe data={profileData} isPersonalView={isPersonalView} />
      </SubSection>
    ) : null;

  return (
    <ElevatedContainer>
      <div data-testid="about-me-container" css={aboutMeContainerStyles}>
        {certificationSection}
        {bioSection}
        {whereToFindMeSection}
        {productsSection}
      </div>
    </ElevatedContainer>
  );
};
export default AboutMe;

enum SocialLink {
  Website = 'website',
  LinkedIn = 'linkedin',
  YouTube = 'youtube',
  X = 'x',
}

const WhereToFindMe = ({
  data,
  isPersonalView,
}: {
  data: UnifiedProfileFragment$data;
  isPersonalView: boolean;
}) => {
  const onClickSocialLink = (linkType: SocialLink, url: string | null) => {
    sendUIEvent({
      source: SCREEN_NAME.PROFILE,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'socialLink',
      attributes: {
        linkType,
      },
      isPersonalView: isPersonalView,
    });
    if (url) {
      window.open(formatUrlForDisplay(url));
    }
  };

  return (
    <div css={iconContainerStyles}>
      {data.websiteUrl && (
        <a
          css={iconBorderStyles}
          onClick={() => onClickSocialLink(SocialLink.Website, data.websiteUrl)}
        >
          <img css={[socialIconStyles, linkIconStyles]} src={LinkIcon}></img>
        </a>
      )}
      {data.linkedinUrl && (
        <a
          css={iconBorderStyles}
          onClick={() =>
            onClickSocialLink(SocialLink.LinkedIn, data.linkedinUrl)
          }
        >
          <img
            css={[socialIconStyles, linkedInIconStyles]}
            src={LinkedInIcon}
          ></img>
        </a>
      )}
      {data.youtubeUrl && (
        <a
          css={iconBorderStyles}
          onClick={() => onClickSocialLink(SocialLink.YouTube, data.youtubeUrl)}
        >
          <img
            css={[socialIconStyles, youtubeIconStyles]}
            src={YoutubeIcon}
          ></img>
        </a>
      )}
      {/* {data.xUrl && (
        <a
          css={iconBorderStyles}
          onClick={() => onClickSocialLink(SocialLink.X, data.xUrl)}
        >
          <img css={[socialIconStyles, xIconStyles]} src={XIcon}></img>
        </a>
      )} */}
    </div>
  );
};

const aboutMeContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.600'),
  '@media (max-width: 767px)': {
    gap: token('space.400'),
  },
});

const hasWhereToFindMe = (data: UnifiedProfileFragment$data) =>
  !!data.websiteUrl || !!data.linkedinUrl;

const iconContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
});

const iconBorderStyles = css({
  display: 'flex',
  marginRight: token('space.200'),
  width: token('space.400'),
  height: token('space.400'),
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: token('color.background.neutral.subtle.hovered'),
  },
  '&:active': {
    backgroundColor: token('color.background.neutral.subtle.pressed'),
  },
});

const socialIconStyles = css({
  margin: 'auto',
});

const linkIconStyles = css({
  width: token('space.400'),
  height: token('space.400'),
});

const linkedInIconStyles = css({
  width: token('space.300'),
  height: token('space.300'),
});

const youtubeIconStyles = css({
  width: '30px',
  height: '30px',
});

// const xIconStyles = css({
//   width: token('space.300'),
//   height: token('space.300'),
// });
